import type { CdropdownOption } from "./Cdropdown.types";
import { DROPDOWN_KEY } from "./constants";
import { findNextEnabledIndex } from "./option-state";

type DropdownKeyboardDirection = 1 | -1;

const DROPDOWN_KEY_DIRECTION: Partial<
  Record<string, DropdownKeyboardDirection>
> = {
  [DROPDOWN_KEY.ArrowDown]: 1,
  [DROPDOWN_KEY.ArrowUp]: -1,
};

interface HandleDropdownKeyboardEventInput {
  event: KeyboardEvent;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  closeDropdown: () => void;
  onActiveIndexChange: (index: number | null) => void;
  onOptionSelect: (option: CdropdownOption) => void;
}

interface HandleDropdownNavigationInput {
  event: KeyboardEvent;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  direction: DropdownKeyboardDirection;
  onActiveIndexChange: (index: number | null) => void;
}

interface HandleDropdownEnterInput {
  event: KeyboardEvent;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  onOptionSelect: (option: CdropdownOption) => void;
}

export function handleDropdownKeyboardEvent({
  event,
  options,
  effectiveActiveIndex,
  closeDropdown,
  onActiveIndexChange,
  onOptionSelect,
}: HandleDropdownKeyboardEventInput): void {
  if (event.defaultPrevented) {
    return;
  }

  if (event.key === DROPDOWN_KEY.Escape) {
    event.preventDefault();
    closeDropdown();
    return;
  }

  if (options.length === 0) {
    return;
  }

  const direction = DROPDOWN_KEY_DIRECTION[event.key];
  if (direction != null) {
    handleDropdownNavigation({
      event,
      options,
      effectiveActiveIndex,
      direction,
      onActiveIndexChange,
    });
    return;
  }

  if (event.key !== DROPDOWN_KEY.Enter) {
    return;
  }

  handleDropdownEnter({
    event,
    options,
    effectiveActiveIndex,
    onOptionSelect,
  });
}

function handleDropdownNavigation({
  event,
  options,
  effectiveActiveIndex,
  direction,
  onActiveIndexChange,
}: HandleDropdownNavigationInput): void {
  event.preventDefault();

  const fallbackStart = direction === 1 ? -1 : options.length;
  const startIndex = effectiveActiveIndex ?? fallbackStart;
  const nextIndex = findNextEnabledIndex({
    options,
    startIndex,
    direction,
  });

  if (nextIndex === -1) {
    return;
  }

  onActiveIndexChange(nextIndex);
}

function handleDropdownEnter({
  event,
  options,
  effectiveActiveIndex,
  onOptionSelect,
}: HandleDropdownEnterInput): void {
  if (
    effectiveActiveIndex == null ||
    effectiveActiveIndex < 0 ||
    effectiveActiveIndex >= options.length
  ) {
    return;
  }

  const activeOption = options[effectiveActiveIndex];
  if (!activeOption || activeOption.disabled) {
    return;
  }

  event.preventDefault();
  onOptionSelect(activeOption);
}
